import { IconButton, Label, mergeStyles, Stack, useTheme } from '@fluentui/react';
import React, { useMemo, useState } from 'react';
import { IToggleOption } from "./IToggleOption.type";

export interface IToggleOptionsProps {
    label?: string;
    options: IToggleOption<any>[];
    onChange?: (event: any, option?: IToggleOption<any>) => void
    defaultSelectedKey?: string | null;
    disabled?: boolean;
    disableClear?: boolean;
    styles?: any;
    unclick?: boolean;
}


const ToggleOptions: React.FunctionComponent<IToggleOptionsProps> = (props) => {
    const theme = useTheme();

    const [selected, setSelectedOption] = useState<IToggleOption<any> | null>(
        props.defaultSelectedKey
            ? props.options.find(q => q.key === props.defaultSelectedKey)!
            : null
    );

    //TODO: move this to application/theme context
    const primaryClass = useMemo(() => {
        return mergeStyles({
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '30px',
            backgroundColor: theme.palette.themePrimary,
            border: '1px solid',
            borderRadius: '8px',
            borderColor: theme.palette.themePrimary,
            cursor: 'pointer',
            color: 'white',
            "&:hover": {
                backgroundColor: 'rgb(0, 90, 158)',
            }
        });
    }, [theme]);

    const secondaryClass = useMemo(() => {
        return mergeStyles({
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '30px',
            cursor: 'pointer',
            border: '1px solid lightgray',
            borderRadius: '8px',
            // border: `1px solid ${theme.palette.themeLighter}`,
            color: 'gray',
            "&:hover": {
                backgroundColor: theme.palette.themeSecondary,
                color: 'white',
            }
        });
    }, [theme]);


    //TODO: move this to application/theme context
    const disabledSecondaryClass = useMemo(() => {
        return mergeStyles({
            display: 'flex',
            height: '30px',
            justifyContent: 'center',
            color: theme.schemes?.default?.semanticColors.buttonText,
            alignItems: 'center',
            backgroundColor: 'transparent',
            border: '1px solid',
            borderColor: theme.schemes?.default?.semanticColors.buttonBackgroundDisabled,
        });
    }, [theme]);

    const disabledPrimaryClass = useMemo(() => {
        return mergeStyles({
            display: 'flex',
            height: '30px',
            justifyContent: 'center',
            color: theme.schemes?.default?.semanticColors.buttonText,
            alignItems: 'center',
            backgroundColor: 'lightgray',
            border: '1px solid',
            borderColor: theme.schemes?.default?.semanticColors.buttonBackgroundDisabled,
        });
    }, [theme]);

    return (
        <div style={{ minWidth: '178px' }}>

            <Label>
                {props.label}
            </Label>


            <Stack
                horizontal
                horizontalAlign='center'
                verticalAlign='center'
                tokens={{ childrenGap: 5 }}
                styles={{ root: { maxWidth: '%100' } }}
            >

                {props.options.map(t => {
                    const isSelected = t.key === selected?.key
                    const className = isSelected
                        ? props.disabled ? disabledPrimaryClass : primaryClass
                        : props.disabled ? disabledSecondaryClass : secondaryClass;

                    return (
                        <Stack.Item
                            key={t.key}
                            grow={1}
                            tabIndex={props.disabled ? undefined : 0}
                            className={props.unclick ? secondaryClass : className}
                            styles={props.styles ? props.styles : { root: { flexBasis: '0' } }}
                            onClick={(event) => {
                                if (props.disabled)
                                    return;

                                setSelectedOption(t);
                                props.onChange?.(event, t);
                            }}
                            onKeyPress={(event) => {
                                if (event.code === 'Space' || event.code === "Enter") {
                                    if (props.disabled)
                                        return;

                                    event.preventDefault();

                                    setSelectedOption(t);
                                    props.onChange?.(event, t);
                                }
                            }}
                        >
                            <div style={{ position: 'relative', userSelect: 'none' }}>
                                {isSelected && !props.unclick &&
                                    <span style={{ position: 'absolute', left: '-17px' }}>
                                        ➤
                                    </span>
                                }
                                {t.text}
                            </div>
                        </Stack.Item>
                    );
                })}
                <div style={{ width: '0px' }}>
                    <IconButton
                        iconProps={{ iconName: 'Clear', styles: { root: { fontSize: 'x-small', padding: '0px' } } }}
                        styles={{ root: { height: '100%', right: '30px', top: '-30px' } }}
                        onClick={() => {
                            setSelectedOption(null);
                            props.onChange?.(null, undefined);
                        }}
                    />
                </div>
            </Stack>
        </div>
    );
}


export default ToggleOptions;